import React from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '../../utils/productUtils';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: Partial<Product> & { grossPrice?: number };
  isPriceGross: boolean;
  isProcessing: boolean;
  isEditing: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTogglePriceMode: () => void;
}

export const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  onClose,
  formData,
  isPriceGross,
  isProcessing,
  isEditing,
  error,
  onSubmit,
  onChange,
  onTogglePriceMode,
}) => {
  const { t } = useTranslation('product');
  // M11b-3: advisor (read-only) sieht die Speichern-Aktion nicht (Defense-in-Depth;
  // die Öffnen-Trigger sind bereits gegated).
  const isReadOnly = useIsReadOnly();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-raised rounded-lg max-w-md w-full p-6 max-h-full overflow-y-auto">
        <h2 className="text-xl font-bold text-primary mb-4">
          {isEditing ? t('editProduct') : t('newProduct')}
        </h2>
        {error && (
          <div className="mb-4 p-3 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded text-[var(--tone-danger-fg)]">
            {error}
          </div>
        )}
        <form onSubmit={onSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-secondary text-sm font-medium mb-2">
              {t('fields.nameRequired')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name || ''}
              onChange={onChange}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block text-secondary text-sm font-medium mb-2">
              {t('fields.description')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={onChange}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
            />
          </div>
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between items-center">
                <label htmlFor="taxRate" className="block text-secondary text-sm font-medium mb-2">
                  {t('fields.taxRateRequired')}
                </label>
              </div>
              <input
                type="number"
                step="0.1"
                id="taxRate"
                name="taxRate"
                value={formData.taxRate || 0}
                onChange={onChange}
                className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <button 
                  type="button" 
                  onClick={onTogglePriceMode}
                  className="py-1 px-2 text-xs font-medium bg-indigo-700 text-[#0C1322] rounded mb-2 hover:bg-indigo-600 transition"
                >
                  {isPriceGross ? t('priceMode.switchToNet') : t('priceMode.switchToGross')}
                </button>
              </div>
              {isPriceGross ? (
                <input
                  type="number"
                  step="0.01"
                  id="grossPrice"
                  name="grossPrice"
                  value={formData.grossPrice || 0}
                  onChange={onChange}
                  className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('placeholders.grossPrice')}
                  required
                />
              ) : (
                <input
                  type="number"
                  step="0.01"
                  id="netPrice"
                  name="netPrice"
                  value={formData.netPrice || 0}
                  onChange={onChange}
                  className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('placeholders.netPrice')}
                  required
                />
              )}
              <div className="text-muted text-xs mt-1">
                {isPriceGross 
                  ? `${t('priceMode.netPriceDisplay')}: ${(formData.netPrice || 0).toFixed(2)} €` 
                  : `${t('priceMode.grossPriceDisplay')}: ${(formData.grossPrice || 0).toFixed(2)} €`}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="sellerArticleId" className="block text-secondary text-sm font-medium mb-2">
              {t('fields.sellerArticleId')}
            </label>
            <input
              type="text"
              id="sellerArticleId"
              name="sellerArticleId"
              value={formData.sellerArticleId || ''}
              onChange={onChange}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="unitCode" className="block text-secondary text-sm font-medium mb-2">
              {t('fields.unitCode')}
            </label>
            <input
              type="text"
              id="unitCode"
              name="unitCode"
              value={formData.unitCode || ''}
              onChange={onChange}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('placeholders.unitCode')}
            />
            <div className="text-muted text-xs mt-1">
              {t('units.description')}
              <ul className="list-disc list-inside mt-1 ml-1 space-y-0.5">
                <li><strong>C62</strong>: {t('units.codes.C62')}</li>
                <li><strong>HUR</strong>: {t('units.codes.HUR')}</li>
                <li><strong>DAY</strong>: {t('units.codes.DAY')}</li>
                <li><strong>KGM</strong>: {t('units.codes.KGM')}</li>
                <li><strong>LTR</strong>: {t('units.codes.LTR')}</li>
                <li><strong>MTR</strong>: {t('units.codes.MTR')}</li>
                <li><strong>KMT</strong>: {t('units.codes.KMT')}</li>
                <li><strong>KWH</strong>: {t('units.codes.KWH')}</li>
                <li><strong>MON</strong>: {t('units.codes.MON')}</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-surface-hover text-primary rounded-md hover:bg-gray-500"
              disabled={isProcessing}
            >
              {t('cancel')}
            </button>
            {/* Speichern — M11b-3: für advisor (read-only) ausgeblendet */}
            {!isReadOnly && (
            <button
              type="submit"
              className={`px-4 py-2 bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-500 flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={isProcessing}
            >
              {isProcessing && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {t('save')}
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}; 